import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Baby, Lock, KeyRound, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const AccountSetupPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const [step, setStep] = useState(1); // 1 = password, 2 = PIN
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [pinData, setPinData] = useState({
    pin: '',
    confirm_pin: ''
  });

  // Password validation
  const passwordValidation = {
    minLength: passwordData.new_password.length >= 8,
    hasUppercase: /[A-Z]/.test(passwordData.new_password),
    hasLowercase: /[a-z]/.test(passwordData.new_password),
    hasNumber: /\d/.test(passwordData.new_password),
    matches: passwordData.new_password === passwordData.confirm_password && passwordData.new_password.length > 0
  };

  const isPasswordValid = Object.values(passwordValidation).every(v => v);

  // PIN validation
  const isPinValid = pinData.pin.length === 5 && 
                     /^\d+$/.test(pinData.pin) && 
                     pinData.pin === pinData.confirm_pin;

  useEffect(() => {
    // Check if user needs to complete setup
    if (user && !user.must_change_password && !user.must_set_pin) {
      // Already completed setup
      navigate(user.role === 'admin' ? '/admin' : '/provider');
    }
  }, [user, navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, getAuthHeaders());
      
      toast.success('Password updated successfully!');
      
      // Check if PIN is also required
      if (user?.must_set_pin) {
        setStep(2);
      } else {
        await refreshUser();
        navigate(user.role === 'admin' ? '/admin' : '/provider');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPinValid) {
      toast.error('Please enter a valid 5-digit PIN');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/timeclock/set-pin`, {
        staff_id: user.id,
        pin: pinData.pin
      }, getAuthHeaders());
      
      toast.success('PIN set successfully! Your account is now ready.');
      await refreshUser();
      navigate(user.role === 'admin' ? '/admin' : '/provider');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Baby className="w-9 h-9 text-primary-foreground" />
          </div>
          <h1 className="font-heading font-bold text-2xl">CareKids WI</h1>
          <p className="text-muted-foreground mt-2">Complete Your Account Setup</p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            step >= 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
          }`}>
            <Lock className="w-5 h-5" />
          </div>
          <div className={`w-16 h-1 ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            step >= 2 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
          }`}>
            <KeyRound className="w-5 h-5" />
          </div>
        </div>

        {/* Step 1: Set Password */}
        {step === 1 && (
          <Card className="card-clay">
            <CardHeader className="text-center">
              <CardTitle className="font-heading flex items-center justify-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Set Your Password
              </CardTitle>
              <CardDescription>
                Create a secure password for your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Temporary Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    placeholder="Enter the password from your email"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                    required
                    data-testid="current-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new_password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a new password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      required
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      required
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                  <p className="text-sm font-medium">Password Requirements:</p>
                  {[
                    { check: passwordValidation.minLength, text: 'At least 8 characters' },
                    { check: passwordValidation.hasUppercase, text: 'One uppercase letter' },
                    { check: passwordValidation.hasLowercase, text: 'One lowercase letter' },
                    { check: passwordValidation.hasNumber, text: 'One number' },
                    { check: passwordValidation.matches, text: 'Passwords match' },
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      {req.check ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className={req.check ? 'text-green-600' : 'text-muted-foreground'}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={!isPasswordValid || loading}
                  data-testid="set-password-btn"
                >
                  {loading ? 'Updating...' : 'Set Password & Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Set PIN */}
        {step === 2 && (
          <Card className="card-clay">
            <CardHeader className="text-center">
              <CardTitle className="font-heading flex items-center justify-center gap-2">
                <KeyRound className="w-5 h-5 text-primary" />
                Set Your Time Clock PIN
              </CardTitle>
              <CardDescription>
                Create a 5-digit PIN for clocking in and out
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePinSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pin">5-Digit PIN</Label>
                  <Input
                    id="pin"
                    type="password"
                    placeholder="Enter 5 digits"
                    maxLength={5}
                    value={pinData.pin}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setPinData({...pinData, pin: value});
                    }}
                    required
                    className="text-center text-2xl tracking-widest"
                    data-testid="pin-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_pin">Confirm PIN</Label>
                  <Input
                    id="confirm_pin"
                    type="password"
                    placeholder="Re-enter 5 digits"
                    maxLength={5}
                    value={pinData.confirm_pin}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setPinData({...pinData, confirm_pin: value});
                    }}
                    required
                    className="text-center text-2xl tracking-widest"
                    data-testid="confirm-pin-input"
                  />
                </div>

                {/* PIN Info */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Your PIN is used for:</strong>
                  </p>
                  <ul className="text-sm text-blue-700 mt-1 ml-4 list-disc">
                    <li>Clocking in and out</li>
                    <li>Taking breaks</li>
                    <li>Time clock verification</li>
                  </ul>
                </div>

                {/* PIN validation feedback */}
                {pinData.pin.length > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    {pinData.pin.length === 5 ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className={pinData.pin.length === 5 ? 'text-green-600' : 'text-amber-600'}>
                      {pinData.pin.length}/5 digits
                    </span>
                    {pinData.confirm_pin.length > 0 && pinData.pin !== pinData.confirm_pin && (
                      <span className="text-red-500 ml-2">PINs do not match</span>
                    )}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={!isPinValid || loading}
                  data-testid="set-pin-btn"
                >
                  {loading ? 'Setting PIN...' : 'Complete Setup'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AccountSetupPage;
